import React, { useState, useRef } from 'react';
import { graphql } from 'gatsby';
import styled, { css } from 'styled-components';
import {
  minBreakpointQuery,
  maxBreakpointQuery,
  sectionMargins,
  standardColours,
  brandColours,
  fontWeights,
  fontSize,
  standardTransition,
  visuallyHidden,
} from '../styles';
import { Container, Heading, HtmlContent } from './ui';
import ProductSpecifications from './ProductSpecifications';
import Content from './Content';
import ModularBlocks from './ModularBlocks';

const StyledTabs = styled.section`
  ${sectionMargins()};
`;

const StyledHeader = styled.header`
  ${maxBreakpointQuery.smedium`
    ${visuallyHidden()};
  `}
`;

const StyledContainer = styled(Container)`
  ${minBreakpointQuery.smedium`
    padding-right: 0;
    padding-left: 0;
  `}
`;

const StyledHeading = styled(Heading)`
  ${minBreakpointQuery.smedium`
    ${visuallyHidden()};
  `}
`;

const StyledList = styled.ul`
  ${minBreakpointQuery.smedium`
    display: flex;
  `}
`;

const StyledItem = styled.li`
  ${minBreakpointQuery.smedium`
    flex-grow: 1;
  `}
`;

const StyledTab = styled.button`
  ${minBreakpointQuery.smedium`
    padding: 20px 30px;
    height: 100%;
    width: 100%;
    font-weight: ${fontWeights.semibold};
    ${fontSize(18)};
    line-height: 1.5;
    background: none;
    border: none;
    transition: ${standardTransition('background-color')};
  `}

  ${minBreakpointQuery.mlarge`
    padding-top: 25px;
    padding-bottom: 25px;
    ${fontSize(20)};
  `}

  ${minBreakpointQuery.xlarge`
    padding-top: 30px;
    padding-bottom: 30px;
    ${fontSize(22)};
  `}

  ${({ active }) => {
    if (active) {
      return css`
        ${minBreakpointQuery.smedium`
          background-color: ${standardColours.lightestGrey};
        `}
      `;
    }
  }}
`;

const StyledOuter = styled.div`
  ${minBreakpointQuery.smedium`
    padding-top: 10px;
    padding-bottom: 10px;
    background-color: ${standardColours.lightestGrey};
  `}

  ${minBreakpointQuery.mlarge`
    padding-top: 20px;
    padding-bottom: 20px;
  `}

  ${minBreakpointQuery.xlarge`
    padding-top: 30px;
    padding-bottom: 30px;
  `}
`;

const StyledInner = styled.section`
  ${maxBreakpointQuery.smedium`
    padding-top: 10px;
    padding-bottom: 10px;
    transition: ${standardTransition('background-color')};
  `}

  ${({ active }) => {
    if (active) {
      return css`
        ${maxBreakpointQuery.smedium`
          background-color: ${standardColours.lightestGrey};
        `}
      `;
    } else {
      return css`
        ${minBreakpointQuery.smedium`
          ${visuallyHidden()};
        `}
      `;
    }
  }}
`;

const StyledSubheading = styled.h3`
  ${maxBreakpointQuery.smedium`
    position: relative;
    padding: 10px 65px 10px 30px;
    font-weight: ${fontWeights.semibold};
    ${fontSize(18)};
    cursor: pointer;
  `}

  ${minBreakpointQuery.smedium`
    ${visuallyHidden()};
  `}

  &:after {
    ${maxBreakpointQuery.smedium`
      content: ${({ active }) => (active ? "'-'" : "'+'")};
      position: absolute;
      top: 0;
      right: 30px;
      width: 20px;
      color: ${brandColours.primary};
      ${fontSize(35)};
      line-height: 1;
      text-align: center;
    `}
  }
`;

const StyledContent = styled.div`
  ${({ active }) => {
    if (!active) {
      return css`
        ${maxBreakpointQuery.smedium`
          ${visuallyHidden()};
        `}
      `;
    }
  }}
`;

const TabInner = ({
  id,
  heading,
  modularBlocks,
  productSpecificationGroups,
  activeTabId,
  setActiveTabId,
}) => {
  const tabInner = useRef();

  return (
    <StyledInner ref={tabInner} active={id === activeTabId}>
      <StyledSubheading
        active={id === activeTabId}
        onClick={() => {
          setActiveTabId(id);
          setTimeout(() => {
            tabInner.current.scrollIntoView({ behavior: 'smooth' });
          });
        }}
      >
        {heading}
      </StyledSubheading>
      <StyledContent active={id === activeTabId}>
        {heading === 'Product Specifications' ? (
          productSpecificationGroups.map(({ heading, items }, id) => (
            <ProductSpecifications key={id} heading={heading} items={items} />
          ))
        ) : typeof modularBlocks === 'string' ? (
          <Content isTabs={true}>
            <HtmlContent html={modularBlocks} />
          </Content>
        ) : (
          <ModularBlocks items={modularBlocks} isTabs={true} />
        )}
      </StyledContent>
    </StyledInner>
  );
};

export const Tabs = ({
  heading,
  items,
  productSpecificationGroups,
  ...props
}) => {
  const [activeTabId, setActiveTabId] = useState(0);

  return (
    <StyledTabs {...props}>
      <StyledHeader>
        <StyledContainer wide={true}>
          <StyledHeading>{heading}</StyledHeading>
          <StyledList>
            {items.map(({ heading }, id) => (
              <StyledItem key={id}>
                <StyledTab
                  active={id === activeTabId}
                  onClick={() => {
                    setActiveTabId(id);
                  }}
                >
                  {heading}
                </StyledTab>
              </StyledItem>
            ))}
          </StyledList>
        </StyledContainer>
      </StyledHeader>
      <StyledOuter>
        {items.map(({ heading, modularBlocks }, id) => (
          <TabInner
            key={id}
            id={id}
            heading={heading}
            modularBlocks={modularBlocks}
            productSpecificationGroups={productSpecificationGroups}
            activeTabId={activeTabId}
            setActiveTabId={setActiveTabId}
          />
        ))}
      </StyledOuter>
    </StyledTabs>
  );
};

export default Tabs;

export const TabFragment = graphql`
  fragment TabFragment on DatoCmsTab {
    heading
    modularBlocks {
      ...ContainedImageModularBlockNarrowFragment
      ...ContentModularBlockTabFragment
      ...ExternalVideoModularBlockFragment
      ...FileDownloadsModularBlockFragment
      ...InternalVideoModularBlockFragment
    }
  }
`;
